import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import download from 'downloadjs';
import { API } from './utils/api';
import axios from './utils/axios';

export async function getClients() {
  const { data } = await axios.get('/clients/clients');
  return data;
}



const fetchClients = async () => {
  const response = await axios.get(API.CLIENTS.VIEW);
  return response.data;
};

const fetchSites = async (clientId) => {
  if (!clientId) return [];
  const response = await axios.get(
    `${API.CLIENTS.LOCATIONS.NON_RECURSIVE}?site__client__id=${clientId}&depth=0`
  );
  return response.data;
};

// ----------
// React Query hooks
// ----------
export const useClients = () =>{
  return useQuery({
    queryKey: ['clients'],
    queryFn: fetchClients,
    staleTime: 1000 * 60 * 10,
    keepPreviousData: true,
  })};

export const useSites = (clientId) =>
  useQuery({
    queryKey: ['sites', clientId],
    queryFn: () => fetchSites(clientId),
    enabled: !!clientId, // only runs when clientId is defined
    staleTime: 1000 * 60 * 10,
    keepPreviousData: true,
  });



//
function extractFilename(contentDisposition) {
  if (!contentDisposition) return null;
  // handles filename*="UTF-8''..." and filename="..."
  const match = /filename\*=UTF-8''([^;]+)|filename="([^"]+)"/i.exec(contentDisposition);
  return match ? decodeURIComponent(match[1] || match[2]) : null;
}

export function useDownloadCertificatesByClient() {
  return useMutation({
    mutationFn: async ({ clientId, siteName, start, end }) => {
      const params = new URLSearchParams();
      params.set('client_id', String(clientId));
      if (siteName) params.set('site_name', String(siteName));
      if (start) params.set('start', format(start, 'yyyy-MM-dd'));
      if (end) params.set('end', format(end, 'yyyy-MM-dd'));

      const url = `${API.INSTRUMENT.CALIBRATIONS}download_by_client/`;
      const res = await axios.get(url, {
        params,
        responseType: 'blob',
        timeout: 60000,
      });

      const cd = res.headers['content-disposition'] || res.headers['Content-Disposition'];
      const serverFilename = extractFilename(cd);
      const fallback = `certificates_${format(new Date(), 'yyyy-MM-dd')}.zip`;

      download(res.data, serverFilename || fallback, 'application/zip');
      return true;
    },
  });
}

export function useDownloadCalibrationSummaryByClient() {
  return useMutation({
    mutationFn: async ({ clientId, siteName, start, end }) => {
      if (!clientId) throw new Error('clientId is required');
      if (!siteName) throw new Error('siteName is required');

      const params = new URLSearchParams();
      params.set('client_id', String(clientId));
      params.set('site_name', String(siteName));
      if (start) params.set('start', format(start, 'yyyy-MM-dd'));
      if (end) params.set('end', format(end, 'yyyy-MM-dd'));

      const url = `${API.INSTRUMENT.CALIBRATIONS}summary_pdf_by_client/`;
      const res = await axios.get(url, {
        params,
        responseType: 'blob',
        timeout: 60000,
      });

      const cd = res.headers['content-disposition'] || res.headers['Content-Disposition'];
      const serverFilename = extractFilename(cd);
      const fallback = `calibration_summary_${format(new Date(), 'yyyy-MM-dd')}.pdf`;

      download(res.data, serverFilename || fallback, 'application/pdf');
      return true;
    },
  });
}


export const listInstruments = async (params = {}) => {
  const res = await axios.get(API.INSTRUMENT.VIEW, { params });
  return res.data;
};

export const useListInstruments = (params = {}) => {
  return useQuery({
    queryKey: ["instruments", params],
    queryFn: () => listInstruments(params),
    staleTime: 1000 * 60 * 5,
    keepPreviousData: true,
  });
};


export const bulkArchiveInstruments = async (ids) => {
  const res = await axios.post(`${API.INSTRUMENT.VIEW}bulk_archive/`, {
    archive: ids,
  });
  return res.data;
};

export const useBulkArchiveInstruments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids) => bulkArchiveInstruments(ids),

    onSuccess: () => {
      // invalidate list queries so dashboard + list pages refresh
      queryClient.invalidateQueries(["instruments"]);
    },
  });
};

export const bulkRestoreInstruments = async (ids) => {
  const res = await axios.post(`${API.INSTRUMENT.VIEW}bulk_restore/`, {
    restore: ids,
  });
  return res.data;
};

export const useBulkRestoreInstruments = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ids) => bulkRestoreInstruments(ids),

    onSuccess: () => {
      // invalidate list queries so dashboard + list pages refresh
      queryClient.invalidateQueries(["instruments"]);
    },
  });
};
